import { Button, Flex, Heading, Icon, SimpleGrid, Text, Wrap, WrapItem } from "@chakra-ui/react";
import type { PageEntityKind } from "./pageContext";
import type { IconType } from "react-icons";
import type { ReactNode } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useProcurementTerms } from "../terminology/useProcurementTerms";
import { FiAlertCircle, FiCalendar, FiCornerUpLeft, FiFilePlus, FiMessageSquare, FiPlus, FiZap } from "react-icons/fi";
import { useSub } from "../subscription/useSub";
import { useContext } from "react";
import { AssistantPanelContext } from "./assistantPanelContext";
import { useCreateProcurementWithAi } from "./useCreateProcurementWithAi";

interface Props {
  onPrompt: (text: string, startButtonLabel?: string) => void;
  pageKind: PageEntityKind | null;
  promptsDisabled?: boolean;
  previousConversationTitle?: string | null;
  onReturnToPrevious?: () => void;
  composer?: ReactNode;
}

interface StartButton {
  icon: IconType;
  label: string;
  start: () => void;
}

export const AssistantEmptyState = ({
  onPrompt,
  pageKind,
  promptsDisabled = false,
  previousConversationTitle,
  onReturnToPrevious,
  composer,
}: Props) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const { canCreateContracts, canCreateEvents, canViewContracts, canUseAssistant } = useSub();
  const panel = useContext(AssistantPanelContext);
  const createProcurementWithAi = useCreateProcurementWithAi();
  const handoff = panel?.handoff;
  const documentScope = panel?.documentScope;
  const source = documentScope?.name ?? handoff?.source;

  const prompted = (icon: IconType, label: string, prompt: string): StartButton => ({
    icon,
    label,
    start: () => onPrompt(prompt, label),
  });
  const startButtons: StartButton[] = !canUseAssistant ? [] : [
    ...(createProcurementWithAi && canCreateEvents
      ? [{ icon: FiPlus, label: t("Create with AI"), start: createProcurementWithAi }]
      : []),
    ...(canCreateContracts
      ? [
          prompted(
            FiFilePlus,
            t("Register contract"),
            t("I want to register a new contract. Help me through it, one step at a time."),
          ),
        ]
      : []),
    ...(canViewContracts
      ? [
          prompted(
            FiCalendar,
            t("Follow up contracts"),
            t(
              "Which of my contracts expire, have an option to extend or need follow-up in the next three months? Show the most urgent first.",
            ),
          ),
        ]
      : []),
    prompted(FiAlertCircle, t("What's urgent?"), terms.assistantUrgentPrompt),
  ];
  const pagePrompts = documentScope ? documentPrompts(t) : canUseAssistant ? pageExamplePrompts(pageKind, t) : null;

  const startChoices = (
    <>
      {startButtons.length > 0 && (
        <SimpleGrid minChildWidth="160px" spacing="2" w="full">
          {startButtons.map((button) => (
            <Button
              key={button.label}
              leftIcon={<Icon as={button.icon} />}
              variant="outline"
              colorScheme="teal"
              whiteSpace="normal"
              h="auto"
              py="3"
              onClick={button.start}
              isDisabled={promptsDisabled}
            >
              {button.label}
            </Button>
          ))}
        </SimpleGrid>
      )}

      {pagePrompts && (
        <Wrap spacing="2" justify="center" mt="5">
          {pagePrompts.map((prompt) => (
            <WrapItem key={prompt}>
              <Button
                variant="ghost"
                size="sm"
                rounded="full"
                borderWidth="1px"
                borderColor="smBorder"
                fontWeight="normal"
                whiteSpace="normal"
                h="auto"
                py="2"
                onClick={() => onPrompt(prompt)}
                isDisabled={promptsDisabled}
              >
                {prompt}
              </Button>
            </WrapItem>
          ))}
        </Wrap>
      )}

      {previousConversationTitle && onReturnToPrevious && (
        <Button
          variant="link"
          colorScheme="teal"
          size="sm"
          mt="6"
          fontWeight="normal"
          whiteSpace="normal"
          leftIcon={<Icon as={FiCornerUpLeft} />}
          onClick={onReturnToPrevious}
        >
          {t("Back to «{{title}}»", { title: previousConversationTitle })}
        </Button>
      )}
    </>
  );

  const heading = source
    ? t("Ask about {{source}}", { source })
    : panel?.introducing
      ? t("Meet your SourceMagnet assistant")
      : terms.assistantHelpHeading;

  if (composer) {
    return (
      <Flex direction="column" align="center" textAlign="center" w="full" px="4">
        <Heading size="md" color="smText" mb="4">
          {heading}
        </Heading>
        <Flex direction="column" w="full" textAlign="start">
          {composer}
        </Flex>
        <Flex direction="column" align="center" w="full" mt="4">
          {startChoices}
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex direction="column" align="center" textAlign="center" py="6" px="4">
      <Flex
        w="56px"
        h="56px"
        rounded="2xl"
        align="center"
        justify="center"
        bg="smPrimarySofter"
        position="relative"
        mb="4"
      >
        <Icon as={FiMessageSquare} color="smPrimary" boxSize="6" />
        <Flex
          position="absolute"
          bottom="-1"
          right="-1"
          w="24px"
          h="24px"
          rounded="full"
          align="center"
          justify="center"
          bg="smPrimarySolid"
          borderWidth="2px"
          borderColor="smBackground"
        >
          <Icon as={FiZap} color="white" boxSize="3" />
        </Flex>
      </Flex>

      <Heading size="md" color="smText" mb="2">
        {heading}
      </Heading>
      <Text fontSize="sm" color="smMuted" maxW="lg" mb="6">
        {documentScope
          ? t("The assistant reads the document before it answers, and names the page it got the answer from.")
          : handoff
            ? t("The assistant has the result in front of it. Ask what you want to know about it, or pick something else below.")
            : terms.assistantScopeIntro}
      </Text>

      {startChoices}
    </Flex>
  );
};

const documentPrompts = (t: TFunction): string[] => [
  t("Summarise this document"),
  t("Which deadlines and obligations does this document set?"),
  t("What does this document say about termination?"),
];

const pageExamplePrompts = (kind: PageEntityKind | null, t: TFunction): string[] | null => {
  switch (kind) {
    case "contract":
      return [
        t("Summarise this contract"),
        t("When does this contract expire, and what is its notice period?"),
        t("Which documents belong to this contract?"),
      ];
    case "v2Event":
      return [
        t("Summarise this procurement"),
        t("How many bids has this procurement received?"),
        t("Which supplier questions in this procurement are unanswered?"),
      ];
    case "project":
      return [
        t("Summarise this project"),
        t("Which procurements and contracts belong to this project?"),
      ];
    default:
      return null;
  }
};
